import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  skipWhile,
  Subject,
  Subscription,
  switchMap,
  tap,
  TapObserver,
  throwError,
} from 'rxjs';
import { Pratica, StatoPratica } from '../../interfaces/pratica';
import { AuthService } from '../_core/services/auth.service';
import { DialogService } from '../_core/services/dialog.service';
import { PraticaProgressService } from '../_core/services/pratica-progress.service';
import { Task, TaskService } from '../_core/services/task.service';
import { HeaderComponent } from '../header/header.component';
import { HeaderModule } from '../header/header.module';
import { LabelPipeModule } from '../_shared/label-pipe/label-pipe.module';
import { SharedModule } from '../_shared/shared.module';
import { StatusBarComponent } from '../_widgets/status-bar/status-bar.component';
import { PraticaAction, PraticaEvent } from './models/pratica-event';
import { TaskManagerService } from './task-manager.service';
import { TaskManagerModule } from '../task-manager/task-manager.module';
import { PraticaDetailComponent } from '../pratica-detail/pratica-detail.component';
import { Slice } from '../header/models/slice';
import { OUTCOME_SLICES } from '../header/outcome-flow-slices';
import { ScoreSummaryComponent } from '../score-summary/score-summary.component';
import { ProductService } from '../_core/services/product.service';
import { MessageDialogComponent } from '../_shared/message-dialog/message-dialog.component';
import { ErrorDialogConfig } from '../_config/error-dialog-config';
import { KpiFactory } from '../requests/models/kpi-factory';
import { PersonaGiuridicaService } from '../_core/services/persona-giuridica.service';
import * as moment from 'moment';
import { EventService } from '../_core/services/event.service';
import { v4 as uuidv4 } from 'uuid';
import { LoaderComponent } from '../_core/components/loader/loader.component';
import { getPraticaFlowSlices } from '../header/pratica-flow-slices';

@Component({
  selector: 'app-pratica',
  standalone: true,
  imports: [
    StatusBarComponent,
    RouterModule,
    HeaderModule,
    SharedModule,
    LabelPipeModule,
    TaskManagerModule,
    PraticaDetailComponent,
    ScoreSummaryComponent,
    LoaderComponent,
  ],
  templateUrl: './pratica.component.html',
  styleUrls: ['./pratica.component.scss'],
})
export class PraticaComponent implements OnInit, OnDestroy {
  @ViewChild(HeaderComponent) headerComponent?: HeaderComponent;

  private praticaProgressSubscription?: Subscription;
  private meSubscription?: Subscription;
  private praticaEventSubscription?: Subscription;
  private signtureStateSubscription?: Subscription;
  private productSubscription?: Subscription;

  private praticaEvent = new Subject<PraticaEvent>();

  private confirmTask: Task | undefined;

  slices: Slice[] = [];
  pieIcons: string[] = [];

  isLoading$ = new Subject<boolean>();

  constructor(
    public praticaProgressService: PraticaProgressService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private taskService: TaskService,
    private dialogService: DialogService,
    private auth: AuthService,
    private taskManager: TaskManagerService,
    private productService: ProductService,
    private pgService: PersonaGiuridicaService,
    private eventService: EventService
  ) {}

  ngOnInit(): void {
    const idPratica = this.activatedRoute.snapshot.paramMap.get('idPratica');
    if (!idPratica) return;
    if (this.praticaProgressService.selectedPratica) {
      this.performPraticaStateChecks(
        this.praticaProgressService.selectedPratica
      );
    }

    this.praticaProgressSubscription =
      this.praticaProgressService.progress$.subscribe((progress) =>
        this.praticaProgressService.calcTotal()
      );

    this.signtureStateSubscription =
      this.praticaProgressService.isReadyForSignature$
        .pipe(
          switchMap((isReady) => {
            return isReady ? this.getTasks() : of(false);
          })
        )
        .subscribe((isReady) => (this.isReadyForSignature = isReady));

    this.subscribeToPraticaEvents();
  }

  ngOnDestroy(): void {
    this.praticaProgressSubscription?.unsubscribe();
    this.meSubscription?.unsubscribe();
    this.praticaEventSubscription?.unsubscribe();
    this.signtureStateSubscription?.unsubscribe();
    this.productSubscription?.unsubscribe();
    this.praticaProgressService.resetPratica();
    this.pgService.cleanData();
    this.taskManager.cleanData();
    this.productService.resetProduct();
  }

  private getTasks() {
    return this.taskService
      .list(
        this.praticaProgressService.selectedPratica!.process!.richiesta,
        'SYSTEM'
      )
      .pipe(
        map((tasks) => {
          this.confirmTask = tasks.find(
            (task) => task.name === 'CONFIRM COMPLETED'
          );
          return !!this.confirmTask;
        })
      );
  }

  isReadyForSignature: boolean = false;

  onPraticaEvent(event: PraticaEvent) {
    const praticaId = this.praticaProgressService.selectedPratica?.id!;
    this.praticaEvent.next(event);
    if (this.praticaProgressService.isFrozen) {
      if (event === PraticaAction.CLOSE) {
        let commands = ['home'];
        if (this.praticaProgressService.selectedPratica?.type === 'AML') {
          commands.push('aml-requests');
        } else {
          commands.push('requests');
        }
        this.router.navigate(commands);
        return;
      } else if (event === PraticaAction.CONFIRM) {
        this.activatedRoute.snapshot;
        this.executeConfirmTask(this.confirmTask);
        return;
      } else {
        this.router.navigate(['./', event], {
          relativeTo: this.activatedRoute,
        });
      }
    }

    this.praticaProgressService.saveProgress();
  }

  private subscribeToPraticaEvents() {
    let eventType: PraticaEvent;
    let prev = 0;
    this.praticaEventSubscription = this.praticaEvent
      .pipe(
        mergeMap((e) => {
          eventType = e;
          return this.praticaProgressService.sectionHasUpdated$.pipe(
            skipWhile((val) => !val || prev === val),
            switchMap((val) => {
              prev = val;
              return this.praticaProgressService.updatePraticaProgress();
            }),
            switchMap((_) => {
              if (eventType === PraticaAction.CONFIRM && this.confirmTask) {
                return this.executeConfirmTask(this.confirmTask);
              } else {
                return of(null);
              }
            }),
            tap((_) => this.showFeedbackAndRedirect(eventType)),
            catchError((error) => {
              console.error(error);
              return of(null);
            })
          );
        })
      )
      .subscribe();
  }

  private executeConfirmTask(task: Task | undefined) {
    let subscriberid = uuidv4();
    return task
      ? forkJoin([
          this.subscribeSse<{ message: string }, { message: string }>(
            subscriberid
          ),
          this.taskService.execute({
            ...task,
            variables: { subscriber: subscriberid },
          }),
        ]).pipe(
          switchMap(() =>
            this.praticaProgressService.getPratica(
              this.praticaProgressService.selectedPratica!.id
            )
          ),
          tap((_) => {
            this.router.navigate(['detail', 'applicant'], {
              queryParams: { spin: true },
              relativeTo: this.activatedRoute,
            });
          })
        )
      : of(null);
  }

  private subscribeSse<R = object, E = any>(
    subscriberId: string
  ): Observable<R | undefined> {
    //this.isLoading$.next(true);
    return this.eventService
      .getServerEvent<R, E>(
        subscriberId,
        'TASK_EVENT_TYPE_OK',
        'TASK_EVENT_TYPE_KO'
      )
      .pipe(
        tap((res) => {
          console.log(res);
          //this.isLoading = false;
        })
      );
  }

  private performPraticaStateChecks(pratica: Pratica) {
    if (this.praticaProgressService.isOutcome) {
      this.productSubscription = this.productService
        .getAnalisiCreditoProduct(pratica.id)
        .pipe(switchMap((_) => this.productService.analisiCreditoProduct$))
        .subscribe({
          next: (product) => {
            this.slices.forEach((slice) => {
              let scoreInfo = KpiFactory.getScoreInfoObject(product?.kpi);
              slice.class = scoreInfo[slice.sectionPath]?.outcomeCssClass;
              slice.hasWarning = scoreInfo[slice.sectionPath]?.hasWarning;
              slice.hasError = scoreInfo[slice.sectionPath]?.hasError;
            });
          },
          error: (err) =>
            this.dialogService.open(
              MessageDialogComponent,
              new ErrorDialogConfig(err)
            ),
        });
      this.slices = OUTCOME_SLICES;
      this.pieIcons = Array.from({ length: 12 }, (v, i) => 'score-points-' + i);
    } else {
      this.slices = getPraticaFlowSlices(pratica.type);
    }
    this.pieIcons = [...this.pieIcons, ...this.slices.map((el) => el.icon)];
  }

  private showFeedbackAndRedirect(eventType: PraticaEvent) {
    let showSuccess =
      eventType === PraticaAction.CLOSE || eventType === PraticaAction.SAVE;

    if (showSuccess) {
      this.dialogService.onSuccess();
    }
    if (eventType === PraticaAction.CLOSE) {
      let commands = ['home'];
      if (this.praticaProgressService.selectedPratica?.type === 'AML') {
        commands.push('aml-requests');
      } else {
        commands.push('requests');
      }
      this.router.navigate(commands);
    } else if (
      eventType !== PraticaAction.SAVE &&
      eventType !== PraticaAction.CONFIRM
    ) {
      let commands = ['./'];
      if (!this.praticaProgressService.isOutcome) {
        commands.push('detail');
      }
      commands.push(eventType);
      this.router.navigate(commands, {
        relativeTo: this.activatedRoute,
      });
    }
  }
}
